import { DeploymentData, DeploymentItem } from "types/deploymentTypes";
import { hostingDeploymentsAxiosInstance } from "./axiosInstances";
import axios from "axios";
import { extractDedicatedDeploymentData, extractSharedDeploymentData } from "utils/Utils";
import { deserializeExtensions } from '../deserializers/extensions.deserializer';
import { deserializeDedicatedCluster } from "deserializers/dedicatedCluster.deserializer";
import { deserializeClusterExtensionLogs } from "deserializers/clusterExtensionLogs.deserializer";

export const deploymentService = {

    validateClusterInformations: async (deploymentData: DeploymentData, useCustomDomain: boolean)=>{
        try {
            // API Payload
            const payload = {
                data: {
                    name: deploymentData.name,
                    domain_name: useCustomDomain ? deploymentData.domainName : `${deploymentData.domainName}.authn-z.link`,
                }
            };
            
            // Create cluster
            const response = await hostingDeploymentsAxiosInstance.post(
                "/dedicated-clusters/validate/cluster-info",
                payload
            );

            return response.data;

        } catch (error) {
            throw error;
        }
    },

    handleCreateCluster: async (payload:any) => {
        try {
            await hostingDeploymentsAxiosInstance.post(
                "/dedicated-clusters",
                payload
            );
        } catch (error) {
            throw error;
        }
    },

    fetchDedicatedClusters: async ()=>{
        try {
            const response = await hostingDeploymentsAxiosInstance.get("/dedicated-clusters");
            const extractedData = (extractDedicatedDeploymentData(response.data) as DeploymentItem[] ).map((item) => ({
                ...item,
                users: "--", // Static placeholder
                sessions: "--",
                idps: "--",
            }));
            return extractedData;
        } catch (error) {
            throw error
        }
    },

    fetchSharedDeployments: async ()=>{
        try {
            const response = await hostingDeploymentsAxiosInstance.get("/shared-deployments");
            const extractedData = (extractSharedDeploymentData(response.data) as DeploymentItem[]).map((item) => ({
                ...item,
                users:"--", // Static value, should be set the number of users of the organizations using the deployment
                sessions: "--", // Static value
                idps: "--", // Static value
            }));
            return extractedData
        } catch (error) {
            throw error
        }
    },

    deleteSharedDeployment: async (id: string, region: string): Promise<any> => {
        try {
            const response = await hostingDeploymentsAxiosInstance.delete(
                `shared-deployments/${id}`,
            );
            return response.data;
        }
        catch (error) {
            throw error;
        }
    },
    
    getConsoleLink: async (deploymentId: string): Promise<any> => {
        try {
            const response = await hostingDeploymentsAxiosInstance.post(
                `shared-deployments/${deploymentId}/open-console`,
                {
                    data: {
                        deploymentId: deploymentId
                    }
                }
            );
            return response.data;
        }
        catch (error) {
            throw error;
        }
    },

    deleteDedicatedDeployment: async (id: string, region: string): Promise<any> => {
        try {
            const response = await hostingDeploymentsAxiosInstance.delete(
                `dedicated-clusters/${id}`,
            );
            return response.data;
        }
        catch (error) {
            throw error;
        }
    },

    checkDeploymentHealth: async (deployment: DeploymentItem) => {
        try {
            const baseUrl = deployment.console_url.replace(/\/admin\/.*\/console$/, '');
            const response = await axios.get(
                `${baseUrl}/management-dashboard/health`
            );
            return response.data.status === 'UP';
        } catch (error) {
            return false;
        }
    },

    cancelSubscription: async (deploymentId: string): Promise<any> =>{
        try {
            const response = await hostingDeploymentsAxiosInstance.post(
                `dedicated-clusters/${deploymentId}/cancel-subscription`
            );
            return response.data;
        }
        catch (error) {
            throw error;
        }
    },

    fetchDedicatedClusterById: async (clusterId:string) : Promise<DeploymentItem | null> =>{
        if(!clusterId) return null;

        try {
            const response = await hostingDeploymentsAxiosInstance.get(`/dedicated-clusters/${clusterId}`);
            const deserializedData = deserializeDedicatedCluster(response.data.data);

            return {
                ...(deserializedData as any),
                users: "--",
                sessions: "--",
                idps: "--",
            } as DeploymentItem;

        } catch (error) {
            throw error;
        }
    },

    
    //=================================
    // Extension APIs
    //=================================

    getAllExtensions: async (
        clusterId: string | undefined,
        type: "community" | "custom" | "all",
        status: string
    ): Promise<any> => {

        if (!clusterId) return;

        try {
            const response = await hostingDeploymentsAxiosInstance.get(`extensions/${clusterId}`,
                {
                    params: {
                        type,
                        status
                    }
                }
            );

            const deserializedExtensions = await deserializeExtensions(response.data);

            return deserializedExtensions;
        } catch (error) {
            throw error;
        }
    },

    installExtension: async (clusterId:string | undefined, extensionId:string, parameters:Record<string, string | boolean> | null) : Promise<any> =>{
        if(!clusterId || !extensionId) return;

        try {
            await hostingDeploymentsAxiosInstance.post(`dedicated-clusters/${clusterId}/extensions/install`,{
                extensionId: extensionId,
                installationParameters: parameters
            });

        } catch (error) {
            throw error;   
        }
    },

    uninstallExtension: async (clusterId:string | undefined, extensionId:string):Promise<any> =>{
        if(!clusterId || !extensionId) return;

        try {
            await hostingDeploymentsAxiosInstance.post(`dedicated-clusters/${clusterId}/extensions/uninstall`,{
                extensionId: extensionId
            });

        } catch (error) {
            throw error;   
        }
    },

    uploadExtension: async (formData: FormData, type?:"cummunity" | "custom") => {
        try{
            await hostingDeploymentsAxiosInstance.post(
                "/extensions/upload",
                formData,
                {
                    params:{
                        type: type && type === "custom" ? "custom" : undefined
                    }
                }
            );
    
        } catch(error){
            throw error;
        }
    },

    getClusterExtensionLogs: async (clusterId:string) => {
        if(!clusterId) return;

        try {
            const response = await hostingDeploymentsAxiosInstance.get(`extensions/${clusterId}/logs`);
            const deserializedData = await deserializeClusterExtensionLogs(response.data);

            return deserializedData;
             
        } catch (error) {
            throw error;
        }
    }
};


export default deploymentService;